import React, { useState, useEffect, useRef, useCallback, Component } from 'react';
import { TelnyxRTC } from '@telnyx/webrtc';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, Loader2, PhoneCall, PhoneIncoming, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Error Boundary component to catch WebRTC errors
class WebRTCErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('WebRTC Error Boundary caught error:', error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error.message || 'WebRTC error');
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Dialog open={this.props.isOpen} onOpenChange={this.props.onClose}>
          <DialogContent className="sm:max-w-md" data-testid="webrtc-error-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Call Service Unavailable
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-6 space-y-4">
              <div className="text-center space-y-2">
                <p className="text-slate-600">Browser calling is not available.</p>
                <p className="text-sm text-slate-500">The call will be placed using your phone instead.</p>
              </div>
              <Button onClick={() => {
                this.setState({ hasError: false, error: null });
                this.props.onClose();
              }}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    return this.props.children;
  }
}

function TelnyxWebRTCCallInner({ 
  isOpen, 
  onClose, 
  phoneNumber, 
  leadName,
  companyName,
  onCallConnected,
  onCallEnded,
  onDurationUpdate,
  onError  // Called when WebRTC fails - allows fallback
}) {
  const [client, setClient] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle, connecting, ringing, active, ended
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [audioSettings, setAudioSettings] = useState(null);
  
  const remoteAudioRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const durationIntervalRef = useRef(null);

  // Fetch audio settings and WebRTC credentials on mount
  useEffect(() => {
    const init = async () => {
      try {
        // Get audio settings
        const audioRes = await axios.get(`${API}/user/preferences/audio_settings`);
        if (audioRes.data?.value) {
          setAudioSettings(audioRes.data.value);
        }
      } catch (err) {
        console.log('No audio settings saved');
      }
    };
    init();
  }, []);

  // Initialize TelnyxRTC client when dialog opens
  useEffect(() => {
    if (!isOpen) return;
    
    let isMounted = true;
    let telnyxClient = null;
    
    // Global error handler for uncaught Telnyx errors
    const handleUncaughtError = (event) => {
      if (event.message && (event.message.includes('getTransceivers') || event.message.includes('TelnyxRTC') || event.message.includes('RTCPeerConnection'))) {
        event.preventDefault();
        event.stopPropagation();
        console.error('Caught Telnyx WebRTC error:', event.message);
        if (isMounted) {
          setError('WebRTC not available. Switching to phone call...');
          setCallState('idle');
          if (onError) onError('WebRTC initialization failed');
        }
        return true;
      }
    };

    // Global unhandledrejection handler for promise errors
    const handleUnhandledRejection = (event) => {
      if (event.reason && String(event.reason).includes('getTransceivers')) {
        event.preventDefault();
        console.error('Caught unhandled rejection:', event.reason);
        if (isMounted) {
          setError('WebRTC not available. Switching to phone call...');
          setCallState('idle');
          if (onError) onError('WebRTC initialization failed');
        }
        return true;
      }
    };
    
    window.addEventListener('error', handleUncaughtError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
    
    const initClient = async () => {
      try {
        if (!isMounted) return;
        setCallState('connecting');
        setError(null);
        
        // Check if browser supports WebRTC
        if (!window.RTCPeerConnection) {
          throw new Error('WebRTC is not supported in this browser');
        }
        
        // Get WebRTC credentials from backend with retry logic for rate limiting
        let credRes;
        let retries = 3;
        while (retries > 0) {
          try {
            credRes = await axios.get(`${API}/telnyx/webrtc-credentials`);
            break;
          } catch (err) {
            if (err.response?.status === 429 && retries > 1) {
              retries--;
              await new Promise(r => setTimeout(r, 2000)); // Wait 2 seconds before retry
              continue;
            }
            throw err;
          }
        }
        
        if (!isMounted) return;
        
        const { username, password } = credRes.data;
        
        if (!username || !password) {
          throw new Error('WebRTC credentials not configured');
        }

        // Verify RTCPeerConnection is working before initializing Telnyx
        try {
          const testPc = new RTCPeerConnection();
          if (typeof testPc.getTransceivers !== 'function') {
            testPc.close();
            throw new Error('RTCPeerConnection.getTransceivers not supported');
          }
          testPc.close();
        } catch (rtcError) {
          console.error('RTCPeerConnection test failed:', rtcError);
          throw new Error('WebRTC not fully supported. Using server-side calling instead.');
        }

        if (!isMounted) return;

        // Create TelnyxRTC client with error handling
        try {
          telnyxClient = new TelnyxRTC({
            login: username,
            password: password,
            ringtoneFile: null,
            ringbackFile: null,
          });
        } catch (telnyxError) {
          console.error('Failed to create TelnyxRTC client:', telnyxError);
          throw new Error('Failed to initialize call client. Please check browser permissions.');
        }

        if (!isMounted) {
          if (telnyxClient) {
            try { telnyxClient.disconnect(); } catch(e) { /* ignore cleanup errors */ }
          }
          return;
        }

        // Set up event listeners
        telnyxClient.on('telnyx.ready', () => {
          console.log('Telnyx WebRTC ready');
          if (isMounted) {
            setCallState('idle');
            makeCall(telnyxClient);
          }
        });

        telnyxClient.on('telnyx.error', (error) => {
          console.error('Telnyx error:', error);
          if (isMounted) {
            setError(error.message || 'Connection error');
            setCallState('idle');
            if (onError) onError(error.message || 'Connection error');
          }
        });

        telnyxClient.on('telnyx.socket.error', (error) => {
          console.error('Socket error:', error);
          if (isMounted) {
            setError('Connection failed. Please check your internet connection.');
            setCallState('idle');
            if (onError) onError('Connection failed');
          }
        });

        telnyxClient.on('telnyx.notification', (notification) => {
          if (isMounted) {
            handleNotification(notification);
          }
        });

        // Connect to Telnyx with error handling and timeout
        const connectPromise = telnyxClient.connect();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Connection timeout')), 10000)
        );
        
        try {
          await Promise.race([connectPromise, timeoutPromise]);
          if (isMounted) {
            setClient(telnyxClient);
          }
        } catch (connectError) {
          console.error('Failed to connect to Telnyx:', connectError);
          throw new Error('Failed to connect to call service. Please try again.');
        }
        
      } catch (err) {
        console.error('Failed to initialize Telnyx:', err);
        if (isMounted) {
          const errorMsg = err.response?.status === 429 
            ? 'Too many requests. Please wait a moment and try again.'
            : err.response?.data?.detail || err.message || 'Failed to connect';
          setError(errorMsg);
          setCallState('idle');
          if (onError) onError(errorMsg);
        }
      }
    };

    // Wrap in setTimeout to ensure React has finished rendering
    const initTimeout = setTimeout(() => {
      initClient();
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      
      // Remove global error handlers
      window.removeEventListener('error', handleUncaughtError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
      
      // Cleanup Telnyx client
      if (telnyxClient) {
        try {
          telnyxClient.disconnect();
        } catch (e) {
          console.log('Disconnect error:', e);
        }
      }
      if (client) {
        try {
          client.disconnect();
        } catch (e) {
          console.log('Disconnect error:', e);
        }
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [isOpen, onError]);

  const handleNotification = useCallback((notification) => {
    const call = notification.call;
    if (!call) return;

    console.log('Call notification:', notification.type, call.state);

    switch (notification.type) {
      case 'callUpdate':
        handleCallStateChange(call);
        break;
      case 'userMediaError':
        console.error('Microphone not found or access denied');
        setError('Microphone not found. Using phone call instead.');
        setCallState('idle');
        // Trigger fallback to server-side calling
        if (onError) onError('Microphone not available');
        break;
      default:
        break;
    }
  }, [onError]);

  const handleCallStateChange = useCallback((call) => {
    const state = call.state;
    console.log('Call state changed:', state);

    switch (state) {
      case 'new':
      case 'trying':
      case 'requesting':
        setCallState('connecting');
        break;
      case 'recovering':
      case 'ringing':
      case 'early':
        setCallState('ringing');
        break;
      case 'active':
        setCallState('active');
        callStartTimeRef.current = Date.now();
        startDurationTimer();
        
        // Attach remote audio stream
        if (call.remoteStream && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = call.remoteStream;
          remoteAudioRef.current.play().catch(e => console.log('Audio play error:', e));
          
          // Apply speaker setting if available
          if (audioSettings?.speaker_id && remoteAudioRef.current.setSinkId) {
            remoteAudioRef.current.setSinkId(audioSettings.speaker_id).catch(e => 
              console.log('Could not set speaker:', e)
            );
          }
        }
        toast.success('Call connected');
        // Notify parent that call is connected with the dialed number
        if (onCallConnected) {
          onCallConnected(phoneNumber);
        }
        break;
      case 'hangup':
      case 'destroy':
      case 'purge':
        setCallState('ended');
        stopDurationTimer();
        if (onCallEnded) {
          onCallEnded(callDuration);
        }
        break;
      default:
        break;
    }
  }, [audioSettings, callDuration, onCallEnded]);

  const makeCall = async (telnyxClient) => {
    if (!phoneNumber) return;
    
    try {
      setCallState('connecting');
      
      // First, check if we can access the microphone
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Successfully got access, now release the stream
        stream.getTracks().forEach(track => track.stop());
      } catch (mediaError) {
        console.error('Microphone access error:', mediaError);
        const errorMsg = mediaError.name === 'NotFoundError' 
          ? 'No microphone found. Using phone call instead.'
          : 'Microphone access denied. Please allow microphone access.';
        setError(errorMsg);
        setCallState('idle');
        if (onError) onError(errorMsg);
        return;
      }
      
      // Clean phone number
      let cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
      if (!cleanNumber.startsWith('+')) {
        cleanNumber = '+1' + cleanNumber;
      }

      // Get from number from backend
      const statusRes = await axios.get(`${API}/telnyx/status`);
      const fromNumber = statusRes.data.phone_number || '+17323941313';

      // Configure audio constraints with preferred mic if available
      const audioConstraints = audioSettings?.microphone_id 
        ? { deviceId: { exact: audioSettings.microphone_id } }
        : true;

      const call = telnyxClient.newCall({
        destinationNumber: cleanNumber,
        callerNumber: fromNumber,
        callerName: 'Taj - JobTalk',
        audio: audioConstraints,
        video: false,
      });

      setCurrentCall(call);
      
    } catch (err) {
      console.error('Failed to make call:', err);
      setError('Failed to initiate call');
      setCallState('idle');
      if (onError) onError('Failed to initiate call');
    }
  };

  const startDurationTimer = () => {
    durationIntervalRef.current = setInterval(() => {
      if (callStartTimeRef.current) {
        const elapsed = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
        setCallDuration(elapsed);
        // Update parent with current duration
        if (onDurationUpdate) {
          onDurationUpdate(elapsed);
        }
      }
    }, 1000);
  };

  const stopDurationTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  };

  const hangUp = () => {
    if (currentCall) {
      try {
        currentCall.hangup();
      } catch (e) {
        console.log('Hangup error:', e);
      }
    }
    setCallState('ended');
    stopDurationTimer();
  };

  const toggleMute = () => {
    if (currentCall) {
      if (isMuted) {
        currentCall.unmuteAudio();
      } else {
        currentCall.muteAudio();
      }
      setIsMuted(!isMuted);
    }
  };

  const toggleDeafen = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !isDeafened;
      setIsDeafened(!isDeafened);
    }
  };

  const handleClose = () => {
    if (currentCall && callState === 'active') {
      hangUp();
    }
    if (client) {
      try {
        client.disconnect();
      } catch (e) {
        console.log('Disconnect error:', e);
      }
    }
    setClient(null);
    setCurrentCall(null);
    setCallState('idle');
    setCallDuration(0);
    setError(null);
    onClose();
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusMessage = () => {
    switch (callState) {
      case 'connecting':
        return 'Connecting...';
      case 'ringing':
        return 'Ringing...';
      case 'active':
        return formatDuration(callDuration);
      case 'ended':
        return `Call ended - ${formatDuration(callDuration)}`;
      default:
        return 'Ready';
    }
  };

  const getStatusColor = () => {
    switch (callState) {
      case 'connecting':
      case 'ringing':
        return 'text-amber-500';
      case 'active':
        return 'text-green-500';
      case 'ended':
        return 'text-slate-500';
      default:
        return 'text-slate-400';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="webrtc-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5 text-green-600" />
            Call in Progress
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center py-6 space-y-4">
          {/* Contact Info */}
          <div className="text-center">
            <p className="text-lg font-semibold">{leadName || 'Unknown'}</p>
            {companyName && <p className="text-sm text-slate-600">{companyName}</p>}
            <p className="text-slate-500">{phoneNumber}</p>
          </div>

          {/* Call Status */}
          <div className={`text-2xl font-mono ${getStatusColor()}`}>
            {callState === 'connecting' && <Loader2 className="h-6 w-6 animate-spin inline mr-2" />}
            {callState === 'ringing' && <PhoneIncoming className="h-6 w-6 animate-pulse inline mr-2" />}
            {getStatusMessage()}
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-center space-y-2">
              <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">
                {error}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setError(null);
                  setCallState('connecting');
                  // Re-trigger initialization
                  if (client) {
                    try { client.disconnect(); } catch(e) { /* ignore cleanup errors */ }
                  }
                  setClient(null);
                }}
                data-testid="retry-call-btn"
              >
                Retry Call
              </Button>
            </div>
          )}

          {/* Call Controls */}
          <div className="flex items-center gap-4 pt-4">
            {/* Mute Button */}
            <Button
              variant={isMuted ? "destructive" : "outline"}
              size="lg"
              className="rounded-full h-14 w-14"
              onClick={toggleMute}
              disabled={callState !== 'active'}
              data-testid="mute-btn"
            >
              {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
            </Button>

            {/* Hang Up Button */}
            <Button
              variant="destructive"
              size="lg"
              className="rounded-full h-16 w-16"
              onClick={hangUp}
              disabled={callState === 'idle' || callState === 'ended'}
              data-testid="hangup-btn"
            >
              <PhoneOff className="h-7 w-7" />
            </Button>

            {/* Speaker Button */}
            <Button
              variant={isDeafened ? "destructive" : "outline"}
              size="lg"
              className="rounded-full h-14 w-14"
              onClick={toggleDeafen}
              disabled={callState !== 'active'}
              data-testid="deafen-btn"
            >
              {isDeafened ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
            </Button>
          </div>

          {/* End call info */}
          {callState === 'ended' && (
            <Button variant="outline" onClick={handleClose} className="mt-4">
              Close
            </Button>
          )}
        </div>

        {/* Hidden audio element for remote stream */}
        <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
      </DialogContent>
    </Dialog>
  );
}

// Export the wrapped component with error boundary
export default function TelnyxWebRTCCall(props) {
  return (
    <WebRTCErrorBoundary 
      isOpen={props.isOpen} 
      onClose={props.onClose}
      onError={props.onError}
    >
      <TelnyxWebRTCCallInner {...props} />
    </WebRTCErrorBoundary>
  );
}
